/**
 * Removes the "Powered by Netlify" badge that Netlify injects into sites on the
 * free plan.
 *
 * Import once from the app entry point:
 *
 *   import { hideNetlifyBadge } from '@avexa/config/hide-netlify-badge'
 *   hideNetlifyBadge()
 *
 * and include `netlifyBadgeCss` in the app's global stylesheet.
 *
 * WHY BOTH CSS AND SCRIPT:
 * The badge is injected by Netlify's edge after the document is served, so it is
 * not in our markup and cannot simply be deleted at build time. CSS alone covers
 * the selectors Netlify uses today; the observer covers the case where they
 * change the markup, which they have done before. Belt and braces, because a
 * badge appearing on a client's production site is the kind of thing nobody
 * notices until the client does.
 *
 * The observer disconnects after 30 seconds. Watching the whole document body
 * forever is a real cost on a long-lived SPA, and the badge is always injected
 * within the first few seconds of load.
 */

/** Selectors Netlify has used for the badge. Kept broad on purpose. */
const BADGE_SELECTORS = [
  '#netlify-badge',
  '.netlify-badge',
  '[id^="netlify-badge"]',
  '[class*="netlify-badge"]',
  'a[href*="netlify.com/?utm_source=badge"]',
  'a[href*="netlify.com"][target="_blank"][rel*="noopener"]',
  'div[style*="netlify"]',
  'netlify-badge',
].join(',')

/**
 * CSS half of the fix. Add this to the app's global stylesheet so the badge is
 * hidden on first paint rather than flickering into view and then disappearing.
 */
export const netlifyBadgeCss = `
/* Netlify free-plan badge — hidden. See packages/config/src/hide-netlify-badge.ts */
#netlify-badge,
.netlify-badge,
[id^='netlify-badge'],
[class*='netlify-badge'],
netlify-badge,
a[href*='netlify.com/?utm_source=badge'] {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
  position: absolute !important;
  width: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
  clip: rect(0 0 0 0) !important;
}
`

function isBadge(el: Element): boolean {
  if (el.matches(BADGE_SELECTORS)) return true

  // Fall back to reading it: a small fixed-position element in a corner whose
  // only link points at netlify.com is the badge whatever it is called.
  const anchor = el.querySelector?.('a[href*="netlify.com"]')
  if (!anchor) return false

  const text = (el.textContent ?? '').toLowerCase()
  return text.includes('netlify')
}

function removeBadges(root: ParentNode = document): void {
  root.querySelectorAll?.(BADGE_SELECTORS).forEach((el) => el.remove())
}

/**
 * Starts hiding the badge. Safe to call more than once, and a no-op outside a
 * browser so it does not break server-side rendering or tests.
 */
export function hideNetlifyBadge(watchMs = 30_000): () => void {
  if (typeof document === 'undefined') return () => {}

  // Inject the CSS immediately, in case the app's stylesheet has not loaded.
  const styleId = 'avexa-hide-netlify-badge'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = netlifyBadgeCss
    document.head.appendChild(style)
  }

  removeBadges()

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue
        const el = node as Element
        if (isBadge(el)) {
          el.remove()
        } else {
          removeBadges(el)
        }
      }
    }
  })

  observer.observe(document.documentElement, { childList: true, subtree: true })

  const stop = () => observer.disconnect()
  window.setTimeout(stop, watchMs)
  return stop
}
